import { dispatchAction } from '@ardoq/rxbeach';
import {
  DEFAULT_TIMELINE_CALLBACKS,
  timelineViewModelOperations,
  getViewRelevantScopeData,
  getGraphNodeOrGroupLabelInViewpointMode,
  ViewModelSourceData,
  DEFAULT_TIMELINE_VIEW_MODEL_SOURCE_DATA,
  TimelineViewSettings,
  getViewGraphHierarchyRootItems,
  FieldsAndItemsSourceData,
  TimelineCallbacks,
} from '@ardoq/timeline2024';
import {
  APIOrganizationUser,
  ArdoqId,
  FilterInterfaceFilter,
  FormattingFilter,
  GroupingRule,
  PersonalSetting,
  ViewIds,
} from '@ardoq/api-types';
import { isPresentationMode } from 'env';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { simpleNotifier } from '@ardoq/dropdown-menu';
import { uniq } from 'lodash';
import {
  getComponentGroupRawItems,
  getTimelineViewGraphHierarchyForViewpointMode,
} from '../viewModel/utils';
import {
  openDetailsDrawer,
  updateDetailsDrawer,
} from 'appLayout/ardoqStudio/detailsDrawer/actions';
import {
  buildGraphFromTraversalAndSearch,
  getAppliedGroups,
  getCanBeUsedAsGroup,
  getVisualizationInterface,
  isGraphModelValid,
  LoadedGraphWithViewpointMode,
  ScopeDataParams,
} from '@ardoq/graph';
import { warmFilterCacheHack } from 'collections/warmFilterCacheHack';
import { buildReferenceFilterContext } from '@ardoq/filter-helpers';
import { dateRangeOperations } from '@ardoq/date-range';
import { ComponentByIdSortDependencies, isTruthy } from '@ardoq/common-helpers';

import { getLoadedPersonalSetting } from 'streams/currentUser/getLoadedPersonalSetting';
import { updatePersonalSetting } from 'streams/currentUser/actions';
import { COMPONENT_ID_ATTRIBUTE } from '@ardoq/global-consts';
import {
  getTimelineSpecificInterface,
  TimelineSpecificInterface,
} from '../viewModel/timelineSpecificInterface';

import { Features, hasFeature } from '@ardoq/features';
import { componentSelectedInQuickExploreMode } from 'traverseOneLevel/actions';
import { ContextShape } from '@ardoq/data-model';
import { Locale } from '@ardoq/locale';
import type { I18nLocale } from '@ardoq/i18n';
import type { ErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';
import { isDiscoverJourney } from 'canvas/isDiscoverJourney';
import { getTimelineTrackingCallbacks } from '../performanceTrackingUtils';
import { componentSelectedInPresentationMode } from 'streams/components/ComponentActions';
import { componentUtils, enhancedScopeDataOperations } from '@ardoq/scope-data';
import { componentOperations, usersOperations } from '@ardoq/core';

export type ViewpointModeTimelineSourceDataArgs = {
  context: ContextShape;
  viewSettings: TimelineViewSettings;
  loadedGraph: LoadedGraphWithViewpointMode;
  locale: Locale;
  i18nLocale: I18nLocale;
  orgUsersById: Record<ArdoqId, APIOrganizationUser>;
  errorInfoBoxLabels: ErrorInfoBoxLabels;
  groupingRules: GroupingRule[];
  formattingAndLabelRules: (FilterInterfaceFilter | FormattingFilter)[];
  /** Duration tracking session started when the source data args were emitted. */
  trackingId?: string;
};

export const warmTimelineFilterCache = (
  loadedGraph: LoadedGraphWithViewpointMode
): void => {
  const enhancedScopeData = loadedGraph.scopeData;
  warmFilterCacheHack({
    scopeData: enhancedScopeData,
    scopeSource: loadedGraph,
    graphModel: enhancedScopeData.graphData,
    referenceFilterContext: buildReferenceFilterContext(enhancedScopeData),
  });
};

const getNeverShowAgain = async () =>
  (await getLoadedPersonalSetting(
    PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_TIMELINE
  )) ?? false;

const setNeverShowAgain = (newValue: boolean) =>
  dispatchAction(
    updatePersonalSetting({
      personalSetting: PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_TIMELINE,
      newValue,
    })
  );

export const buildViewpointModeViewModelSourceData = (
  state: ViewModelSourceData,
  {
    context: { componentId: contextComponentId, sort },
    viewSettings,
    loadedGraph,
    locale: userLocale,
    i18nLocale,
    orgUsersById,
    errorInfoBoxLabels,
    groupingRules,
    formattingAndLabelRules,
    trackingId,
  }: ViewpointModeTimelineSourceDataArgs
): ViewModelSourceData => {
  const enhancedScopeData = loadedGraph.scopeData;
  const graph = enhancedScopeData.graphData;

  const scopeDataParams: ScopeDataParams = {
    enhancedScopeData,
    formattingFilters: formattingAndLabelRules,
    groupingRules,
    i18nLocale,
    formatUserFieldFn: val =>
      usersOperations.formatUserField(orgUsersById, val),
  };

  const userDefinedGrouping = groupingRules;

  const componentDoubleClickHandler = (componentId: ArdoqId) => {
    if (!componentUtils.isComponent(enhancedScopeData, componentId)) {
      return;
    }

    if (isPresentationMode()) {
      dispatchAction(componentSelectedInPresentationMode({ id: componentId }));
      return;
    }

    if (isDiscoverJourney() || hasFeature(Features.CANVAS_PROTOTYPE)) {
      dispatchAction(componentSelectedInQuickExploreMode(componentId));
      return;
    }

    dispatchAction(openDetailsDrawer([componentId]));
  };

  const componentSingleClickHandler = (componentId: string): void => {
    dispatchAction(updateDetailsDrawer([componentId]));
  };

  const hasGroupingStyles = true;

  const { viewInterface: previousViewInterface } = state;

  const viewInterface = {
    ...previousViewInterface,
    ...getVisualizationInterface<TimelineSpecificInterface>({
      scopeDataParams,
      getViewSpecificInterface: getTimelineSpecificInterface,
    }),
    componentDoubleClickHandler,
    componentSingleClickHandler,
    trackingCallbacks: undefined,
  };

  const updatedState: ViewModelSourceData = {
    ...state,
    viewInterface,
    contextComponentId,
    graph,
    hasGroupingStyles,
    sourceDataViewSettings: viewSettings,
    i18nLocale,
    errorInfoBoxLabels,
  };

  if (!loadedGraph.scopeComponentIds.length) {
    return {
      ...updatedState,
      isViewEmpty: true,
    };
  }

  if (!isGraphModelValid(viewInterface, graph)) {
    // if graph is invalid, typically this means the graphModel$ has not updated itself with the latest state
    // of the References collection, we therefore avoid rebuilding the graph based on other updates
    return updatedState;
  }

  const hasUserDefinedGroups = Boolean(userDefinedGrouping.length);

  // graph model traversal with applied grouping rules hierarchy
  const traversalAndGroupingResult = buildGraphFromTraversalAndSearch({
    graphInterface: viewInterface,
    viewId: ViewIds.TIMELINE,
    graph,
    groupingRules: getAppliedGroups(userDefinedGrouping),
    loadedGraph,
    canBeUsedAsGroup: getCanBeUsedAsGroup(
      viewInterface,
      loadedGraph,
      hasUserDefinedGroups
    ),
    i18nLocale,
  });

  const componentGroupsRawGraphItems = getComponentGroupRawItems(
    traversalAndGroupingResult.groupMap,
    new Set(traversalAndGroupingResult.componentMap.keys())
  );
  const componentGroupModelIds = componentGroupsRawGraphItems.map(
    ({ modelId }) => modelId
  );
  const allComponentIds = uniq(
    loadedGraph.scopeComponentIds.concat(componentGroupModelIds)
  ).filter(id => componentUtils.isComponent(enhancedScopeData, id));

  const componentAttributesEntries = allComponentIds
    .map(id => enhancedScopeDataOperations.getComponent(enhancedScopeData, id))
    .filter(isTruthy)
    .map(componentAttributes => [componentAttributes._id, componentAttributes]);

  const componentAttributesById = Object.fromEntries(
    componentAttributesEntries
  );

  const sortDependencies: ComponentByIdSortDependencies = {
    componentAttributesById,
    sort,
    isDateRangeField: dateRangeOperations.isCombinedDateRangeFieldNames(
      sort.attr
    ),
    userLocale,
  };

  // abstraction of the view graph hierarchy to graph nodes with temporary ids
  const { viewGraphHierarchy } = getTimelineViewGraphHierarchyForViewpointMode({
    allComponentIds,
    loadedGraph,
    hasUserDefinedGroups,
    traversalAndGroupingResult,
    shouldShapeHierarchyByTraversal: hasFeature(
      Features.VISUALIZATIONS_TMP_TRAVERSAL_SHAPED_HIERARCHY
    ),
  });

  viewGraphHierarchy.forEach(({ rawGraphItem, graphItem }) => {
    if (rawGraphItem) {
      graphItem.setItemLabels(
        getGraphNodeOrGroupLabelInViewpointMode({
          labelsInterface: viewInterface,
          rawGraphItem,
          isComponent: id => componentUtils.isComponent(enhancedScopeData, id),
          i18nLocale,
        })
      );
    }
  });

  const {
    viewRelevantWorkspacesScopeData,
    viewRelevantComponentsScopeData,
    workspaceAndFieldsRegistry,
  } = getViewRelevantScopeData({
    allComponentIds,
    viewInterface,
    childrenByParent: componentOperations.buildChildrenByParent(
      enhancedScopeData.components
    ),
  });

  const fieldsMetaData = timelineViewModelOperations.getFieldsMetaData({
    viewRelevantWorkspacesScopeData,
    viewSettings,
    workspaceAndFieldsRegistry,
  });

  const { errors, hasClones } = traversalAndGroupingResult;

  const fieldsAndItemsSourceData: FieldsAndItemsSourceData = {
    viewGraphHierarchy,
    startSetNodes: getViewGraphHierarchyRootItems(viewGraphHierarchy),
    ...fieldsMetaData,
  };

  const metrics = {
    componentCount: allComponentIds.length,
    referenceCount: 0, // Timeline does not show references
    workspaceCount: workspaceAndFieldsRegistry.size,
  };

  const trackingCallbacks = trackingId
    ? getTimelineTrackingCallbacks(trackingId, metrics)
    : undefined;

  return {
    ...updatedState,
    errors,
    hasClones,
    viewRelevantComponentsScopeData,
    workspaceAndFieldsRegistry,
    fieldsAndItemsSourceData,
    sortDependencies,
    contextComponentId,
    isViewEmpty: !fieldsAndItemsSourceData.startSetNodes.length,
    users: enhancedScopeData.organizationUsers,
    viewInterface: { ...viewInterface, trackingCallbacks },
  };
};

export const getInitialViewpointModeSourceDataState = (
  viewInstanceId: string
): ViewModelSourceData => {
  const timelineCallbacks: TimelineCallbacks = {
    ...DEFAULT_TIMELINE_CALLBACKS,
    openDateRangeViewSetting: simpleNotifier(),
    openMilestonesViewSetting: simpleNotifier(),
    onViewSettingsUpdate,
    isPresentationMode,
    getNeverShowAgain,
    setNeverShowAgain,
  };

  return {
    ...DEFAULT_TIMELINE_VIEW_MODEL_SOURCE_DATA,

    viewInterface: {
      ...getVisualizationInterface<TimelineSpecificInterface>({
        getViewSpecificInterface: getTimelineSpecificInterface,
      }),

      features: {
        isModernizedStyle: true,
        isNewConditionalFormatting: true,
      },
      timelineCallbacks,
    },
    viewInstanceId,
    contextMenuDataAttributeName: COMPONENT_ID_ATTRIBUTE,
    users: [],
  };
};
